import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BellRing } from 'lucide-react';
import {
  DataState,
  DataStateError,
  TableRowsSkeleton,
  Text,
  Title,
} from '@/shared/ui';
import { EmptyState } from '@/shared/ui/components/basic/EmptyState';
import { useAlerts, useAlertMutations } from '../hooks/useAlerts';
import { AlertRow } from '../components/AlertRow';

/**
 * Alertas — the recurring-alert registry for a workspace.
 *
 * Lists the broker's alerts with their frequency, target, and active/inactive
 * status; each row's switch toggles the alert (a real persisted write) and the
 * delete affordance removes it. Reachable from the AIDock "Alertas" entry at
 * `/app/agencies/{agencyId}/alerts`. Alerts are created conversationally by the
 * assistant ("Criar alerta"); scheduled firing is a separate, deferred
 * capability.
 *
 * @returns The alerts list page.
 */
export function AlertsPage() {
  const { agency_id: agencyId = '' } = useParams<{ agency_id: string }>();
  const { isLoading, isError, alerts, refresh } = useAlerts(agencyId);
  const { toggle, remove, isToggling, isRemoving } = useAlertMutations(agencyId);
  const busy = isToggling || isRemoving;
  const { t } = useTranslation('features/assistant');

  return (
    <div className="flex flex-1 min-h-0 w-full flex-col overflow-hidden bg-[var(--surface-secondary)] font-[family-name:var(--font-family)]">
      <div className="flex flex-1 min-h-0 w-full flex-col gap-[var(--space-4)] overflow-auto p-[var(--space-5)] px-[var(--space-6)]">
        <div className="flex items-center gap-(--space-3)">
          <Text
            component="span"
            className="flex size-9 shrink-0 items-center justify-center rounded-(--radius-md) bg-(--selection-selected) text-(--seed-base)"
            aria-hidden
          >
            <BellRing width={20} height={20} />
          </Text>
          <div className="flex flex-col">
            <Title order={3} style={{ fontSize: 'inherit', fontWeight: 'var(--font-bold)' }}>{t('alerts.title')}</Title>
            <Text variant="meta" tone="secondary">
              {t('alerts.subtitle')}
            </Text>
          </div>
        </div>

        <DataState
          isLoading={isLoading}
          isError={isError}
          isEmpty={false}
          loadingFallback={<TableRowsSkeleton rows={4} />}
          errorState={<DataStateError message={t('alerts.loadError')} onRetry={refresh} />}
        >
          {alerts.length === 0 ? (
            <EmptyState
              icon={<BellRing width={28} height={28} />}
              title={t('alerts.emptyTitle')}
              description={t('alerts.emptyDescription')}
            />
          ) : (
            <div
              className="flex flex-col gap-(--space-3)"
              data-testid="alerts-list"
            >
              {alerts.map((alert) => (
                <AlertRow
                  key={alert.alert_id}
                  alert={alert}
                  onToggle={toggle}
                  onDelete={remove}
                  disabled={busy}
                />
              ))}
            </div>
          )}
        </DataState>
      </div>
    </div>
  );
}

export default AlertsPage;
